import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  Check,
  Loader2,
  MessageSquare,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import type { ConversationSummary } from '../../lib/api';

interface ConversationSidebarProps {
  conversations: ConversationSummary[];
  activeId: string | null;
  loading: boolean;
  query: string;
  showArchived: boolean;
  onQueryChange: (value: string) => void;
  onToggleArchived: () => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (conversation: ConversationSummary) => void;
  onToggleArchive: (conversation: ConversationSummary) => void;
  onDelete: (conversation: ConversationSummary) => void;
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ConversationRow({
  conversation,
  active,
  onSelect,
  onRename,
  onTogglePin,
  onToggleArchive,
  onDelete,
}: {
  conversation: ConversationSummary;
  active: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (conversation: ConversationSummary) => void;
  onToggleArchive: (conversation: ConversationSummary) => void;
  onDelete: (conversation: ConversationSummary) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conversation.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== conversation.title) onRename(conversation.id, next);
    setEditing(false);
  };

  return (
    <li
      className={`group relative rounded-lg border transition ${
        active
          ? 'border-zt-primary/50 bg-zt-primary/10'
          : 'border-transparent hover:border-zt-border hover:bg-zt-elevated/60'
      }`}
    >
      {editing ? (
        <div className="flex items-center gap-1 px-2 py-2">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="min-w-0 flex-1 rounded border border-zt-border bg-zt-bg px-2 py-1 text-sm text-slate-100 focus:border-zt-primary focus:outline-none"
          />
          <button
            type="button"
            onClick={commit}
            className="rounded p-1 text-zt-primary hover:bg-zt-bg"
            aria-label="Save title"
          >
            <Check size={14} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded p-1 text-zt-muted hover:bg-zt-bg"
            aria-label="Cancel rename"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onSelect(conversation.id)}
          className="w-full px-3 py-2.5 text-left"
        >
          <div className="flex items-center gap-2">
            {conversation.pinned ? (
              <Pin size={12} className="shrink-0 text-zt-primary" />
            ) : (
              <MessageSquare size={12} className="shrink-0 text-zt-muted" />
            )}
            <span
              className={`min-w-0 flex-1 truncate text-sm ${
                active ? 'font-medium text-white' : 'text-slate-200'
              }`}
            >
              {conversation.title}
            </span>
            <span className="shrink-0 text-[10px] text-zt-muted group-hover:opacity-0">
              {relativeTime(conversation.updatedAt)}
            </span>
          </div>
          {conversation.preview && (
            <p className="mt-0.5 truncate pl-5 text-xs text-zt-muted">{conversation.preview}</p>
          )}
        </button>
      )}

      {!editing && (
        <div className="absolute right-1.5 top-1.5 hidden items-center gap-0.5 rounded-md bg-zt-surface/95 px-1 py-0.5 shadow group-hover:flex">
          <button
            type="button"
            onClick={() => onTogglePin(conversation)}
            className="rounded p-1 text-zt-muted hover:text-zt-primary"
            aria-label={conversation.pinned ? 'Unpin conversation' : 'Pin conversation'}
            title={conversation.pinned ? 'Unpin' : 'Pin'}
          >
            {conversation.pinned ? <PinOff size={13} /> : <Pin size={13} />}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(conversation.title);
              setEditing(true);
            }}
            className="rounded p-1 text-zt-muted hover:text-slate-100"
            aria-label="Rename conversation"
            title="Rename"
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            onClick={() => onToggleArchive(conversation)}
            className="rounded p-1 text-zt-muted hover:text-slate-100"
            aria-label={conversation.archived ? 'Restore conversation' : 'Archive conversation'}
            title={conversation.archived ? 'Restore' : 'Archive'}
          >
            {conversation.archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
          </button>
          <button
            type="button"
            onClick={() => onDelete(conversation)}
            className="rounded p-1 text-zt-muted hover:text-zt-danger"
            aria-label="Delete conversation"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </li>
  );
}

export default function ConversationSidebar({
  conversations,
  activeId,
  loading,
  query,
  showArchived,
  onQueryChange,
  onToggleArchived,
  onSelect,
  onNew,
  onRename,
  onTogglePin,
  onToggleArchive,
  onDelete,
}: ConversationSidebarProps) {
  const { pinned, recent } = useMemo(
    () => ({
      pinned: conversations.filter((c) => c.pinned),
      recent: conversations.filter((c) => !c.pinned),
    }),
    [conversations]
  );

  const rowProps = { onSelect, onRename, onTogglePin, onToggleArchive, onDelete };

  return (
    <aside className="flex h-full min-h-0 flex-col rounded-xl border border-zt-border bg-zt-surface">
      <div className="space-y-2 border-b border-zt-border p-3">
        <button
          type="button"
          onClick={onNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-zt-primary px-3 py-2 text-sm font-medium text-zt-bg transition hover:bg-zt-accent"
        >
          <Plus size={15} /> New chat
        </button>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zt-muted" />
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search conversations"
            className="w-full rounded-lg border border-zt-border bg-zt-bg py-1.5 pl-8 pr-2 text-sm text-slate-200 placeholder-zt-muted focus:border-zt-primary focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={onToggleArchived}
          className={`text-xs transition ${
            showArchived ? 'text-zt-primary' : 'text-zt-muted hover:text-slate-300'
          }`}
        >
          {showArchived ? 'Viewing archived — show active' : 'Show archived'}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading && conversations.length === 0 && (
          <div className="flex items-center gap-2 p-3 text-sm text-zt-muted">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && conversations.length === 0 && (
          <p className="p-3 text-sm text-zt-muted">
            {query ? 'No conversations match that search.' : 'No conversations yet.'}
          </p>
        )}

        {pinned.length > 0 && (
          <>
            <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
              Pinned
            </p>
            <ul className="space-y-1">
              {pinned.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  active={c.id === activeId}
                  {...rowProps}
                />
              ))}
            </ul>
          </>
        )}

        {recent.length > 0 && (
          <>
            {pinned.length > 0 && (
              <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
                Recent
              </p>
            )}
            <ul className="space-y-1">
              {recent.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  active={c.id === activeId}
                  {...rowProps}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
}
